<template>
  <div class="broker-dashboard">
    <!-- Loading Overlay -->
    <LoadingOverlay
      :loading="isLoading"
      :message="loadingMessage"
    />

    <!-- Skeleton Loader for initial load -->
    <SkeletonLoader
      v-if="isInitialLoading"
      type="dashboard"
    />

    <!-- Main Content (hidden during initial load) -->
    <div v-show="!isInitialLoading">
    <!-- Welcome Section -->
    <v-row class="mb-15 align-center" no-gutters >
        <!-- Right: Support + Releases -->
      <v-col cols="12" md="4" class="pb-0" order="1" order-md="2" >
        <div class="d-flex justify-end gap-3 mt-md-0 justify-start justify-md-end">
          <!-- Releases card -->
          <v-card
            class="quick-card"
            outlined
            :ripple="false"
            @click="openReleases"
          >
            <v-card-text class="d-flex align-center">
              <v-icon size="18" class="mr-2" color="#14161B">fa-regular fa-rocket</v-icon>
              <span class="font-weight-medium">{{ $t('broker.dashboard.announcements') || 'Announcements' }}</span>
            </v-card-text>
          </v-card>
          <!-- Support card -->
          <v-card
            class="quick-card no-underline"
            outlined
            :ripple="false"
            @click="openSupport"                      
          >
            <v-card-text class="d-flex align-center">
              <v-icon size="18" class="mr-2"  color="#14161B">fa-regular fa-question-circle</v-icon>
              <span class="font-weight-medium">{{ $t('broker.app.navigation.support') || 'Support' }}</span>
            </v-card-text>
          </v-card>     
        </div>
      </v-col>

      <v-col cols="12" md="8" class="pb-0" order="2" order-md="1">
        <div class="d-flex align-center">
          <v-avatar size="78" class="mr-4 avatar-border">
            <v-img :src="stableProfilePicture || require('@/assets/default-picture.svg')" />
          </v-avatar>
          <div>
            <h1 class="text-h1 font-weight-bold mb-0">{{ $t('broker.dashboard.welcome.greeting', { name: userName }) || `Hi ${userName}` }}</h1>
            <h3 class="text-h3 black--text font-weight-medium text--darken-1 mb-0 mt-1">{{ $t('broker.dashboard.welcome.subtitle') || 'Welcome back again' }}</h3>
          </div>
        </div>
      </v-col>


    </v-row>

    

    <!-- Dashboard Header -->
    <v-row class="my-0">
      <v-col cols="12" class="py-0 mb-10">
        <h2 class="text-h2 font-weight-bold">{{ $t('broker.dashboard.performanceMetrics.title') || 'Performance Metrics' }}</h2>
        <p class="text-body-1 black--text text--darken-2 mt-3 mb-0">
          {{ $t('broker.dashboard.performanceMetrics.description') || 'Heads up! This is your all-in-one stats hub—global and country-level insights await. Tweak the filters to drill down into exactly what you need.' }}
        </p>
      </v-col>
    </v-row>

    <!-- Overview Section -->
    <v-row class="mb-4 mt-0">
      <v-col cols="12" class="py-0">
        <!-- Filter Dropdown -->
        <div class="d-flex mb-5">
          <v-select
            ref="filterSelect"
            v-model="selectedFilter"
            :items="timeFilters"
            item-text="label"
            item-value="value"
            outlined
            dense
            hide-details
            class="time-filter-select"
            @change="handleFilterChange"
            @blur="forceBlur"
          />
        </div>
        <h3 class="text-h3 font-weight-medium mb-2">{{ $t('broker.dashboard.overview.title') || 'Overview' }}</h3>
      </v-col>
      <v-col
        v-for="metric in overviewMetrics"
        :key="metric.key"
        cols="12"
        sm="6"
        md="4"
        lg
        class="d-flex flex-grow-1"
      >
        <v-card class="metric-card flex-grow-1" outlined elevation="0">
          <v-card-text class="pa-3">
            <div class="text-left">
              <div class="text-h5 black--text mb-1">
                <h2 class="font-weight-bold d-inline">{{ metric.displayValue }}</h2>
                <span v-if="metric.displayUnit" class="text-body-2 d-inline black--text text--darken-1 ml-1">{{ metric.displayUnit }}</span>
              </div>
              <div class="text-body-2 d-inline black--text text--darken-1">
                {{ metric.label }}
                <v-icon color="black darken-2" size="14" class="ml-1">{{ metric.icon }}</v-icon>
              </div>
              <div class="text-body-2 grey--text text--lighten-1">
                {{ metric.period }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Performance by Country Section -->
    <v-row class="mb-4">
      <v-col cols="12" class="pb-0">
        <h3 class="text-h3 font-weight-medium mb-0 mt-7">{{ $t('broker.dashboard.performanceByCountry.title') || 'Performance by Country' }}</h3>
      </v-col>
    </v-row>

    <!-- Map and Side Cards Row -->
    <v-row>
      <!-- World Map -->
      <v-col cols="12" lg="8" class="pr-lg-2 pt-0">
        <v-card class="map-card" color="transparent" flat>
          <v-card-text>
            <svg-world-map
              :countries="geographicData"
              @country-click="onCountryClick"
              ref="worldMap"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Side Cards Column -->
      <v-col cols="12" lg="4" class="pl-lg-2 d-flex flex-column pt-0">
        <div class="d-flex flex-column">
          <!-- Total Traders Card -->
          <v-card class="side-metric-card mb-3" outlined elevation="0">
            <v-card-text class="pa-3">
              <div class="d-flex align-center justify-space-between">
                <div class="text-h2 font-weight-bold black--text">
                  {{ formatNumber(traderStats.totalTraders) }}
                </div>
                <div class="text-body-2 black--text d-flex align-center">
                  {{ $t('broker.dashboard.traderStats.totalTraders') || 'Total Traders' }}
                  <v-icon color="black" size="14" class="ml-4">fa-regular fa-user</v-icon>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Live Accounts Card -->
          <v-card class="side-metric-card mb-3" outlined elevation="0">
            <v-card-text class="pa-3">
              <div class="d-flex align-center justify-space-between">
                <div class="text-h2 font-weight-bold black--text">
                  {{ traderStats.liveAccountsPercentage }}%
                </div>
                <div class="text-body-2 black--text d-flex align-center">
                  {{ $t('broker.dashboard.traderStats.liveAccountsPercentage') || 'Live Accounts %' }}
                  <v-icon color="black" size="14" class="ml-4">fa-regular fa-badge-check</v-icon>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Demo Accounts Card -->
          <v-card class="side-metric-card" outlined elevation="0">
            <v-card-text class="pa-3">
              <div class="d-flex align-center justify-space-between">
                <div class="text-h2 font-weight-bold black--text">
                  {{ traderStats.demoAccountsPercentage }}%
                </div>
                <div class="text-body-2 black--text d-flex align-center">
                  {{ $t('broker.dashboard.traderStats.demoAccountsPercentage') || 'Demo Accounts %' }}
                  <v-icon color="black" size="14" class="ml-4">fa-regular fa-book</v-icon>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-col>
    </v-row>

    <!-- Custom Date Range Dialog -->
    <v-dialog v-model="customDateDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h3">{{ $t('broker.dashboard.dateRangeDialog.title') || 'Select Date Range' }}</span>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="customDateRange.start"
                      :label="$t('broker.dashboard.dateRangeDialog.startDate') || 'Start Date'"
                      prepend-icon="fa-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      color="primary"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="customDateRange.start"
                    @input="startDateMenu = false"
                    color="primary"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="customDateRange.end"
                      :label="$t('broker.dashboard.dateRangeDialog.endDate') || 'End Date'"
                      prepend-icon="fa-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      color="primary"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="customDateRange.end"
                    @input="endDateMenu = false"
                    color="primary"
                    :min="customDateRange.start"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="secondary" class="secondary" @click="cancelCustomDate">{{ $t('broker.dashboard.dateRangeDialog.cancel') || 'Cancel' }}</v-btn>
          <v-btn color="primary" text @click="applyCustomDate" :disabled="!isDateRangeValid">
            {{ $t('broker.dashboard.dateRangeDialog.apply') || 'Apply' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </div>
  </div>
</template>

<script>
import dashboardService from '../services/dashboard.service'
import SvgWorldMap from '../components/SvgWorldMap.vue'
import LoadingOverlay from '../components/LoadingOverlay.vue'
import SkeletonLoader from '../components/SkeletonLoader.vue'

export default {
  name: 'BrokerDashboard',
  components: {
    SvgWorldMap,
    LoadingOverlay,
    SkeletonLoader
  },
  data() {
    return {
      loading: false,
      isLoading: false,
      isInitialLoading: true,
      loadingMessage: 'Loading dashboard data...',
      userName: 'Jake Robinson',
      stableProfilePicture: null,
      selectedFilter: 'thisMonth',
      selectedFilterLabel: 'This Month',
      lastNonCustomFilter: 'thisMonth',
      customDateDialog: false,
      startDateMenu: false,
      endDateMenu: false,
      customDateRange: {
        start: null,
        end: null
      },
      performanceMetrics: {},
      traderStats: {
        totalTraders: 0,
        liveAccountsPercentage: 0,
        demoAccountsPercentage: 0,
        newTradersThisMonth: 0,
        activeTradersToday: 0
      },
      geographicData: [],
      dashboardData: null
    }
  },
  computed: {
    timeFilters() {
      return [
        { label: this.$t('broker.dashboard.filters.thisMonth') || 'This Month', value: 'thisMonth' },
        { label: this.$t('broker.dashboard.filters.thisQuarter') || 'This Quarter', value: 'thisQuarter' },
        { label: this.$t('broker.dashboard.filters.thisYear') || 'This Year', value: 'thisYear' },
        { label: this.$t('broker.dashboard.filters.custom') || 'Custom', value: 'custom' }
      ]
    },
    overviewMetrics() {
      const metrics = this.performanceMetrics
      return [
       /*  {
          key: 'volume',
          label: this.$t('broker.dashboard.metrics.volumeOperated') || 'Volume Operated',
          icon: 'fa-regular fa-analytics',
          displayValue: metrics.volumeOperated ? metrics.volumeOperated.value : '0',
          displayUnit: this.$t('broker.dashboard.metrics.units.lots') || 'Lots',
          period: this.getPeriodText()
        }, */
        {
          key: 'generated',
          label: this.$t('broker.dashboard.metrics.generatedLots') || 'Generated Lots',
          icon: 'fa-regular fa-sack-dollar',
          displayValue: this.formatNumber(metrics.generatedLots?.value || 0),
          displayUnit: this.$t('broker.dashboard.metrics.units.lots') || 'Lots',
          period: this.getPeriodText()
        },
        {
          key: 'trades',
          label: this.$t('broker.dashboard.metrics.liveTrades') || 'Live Trades',
          icon: 'fa-regular fa-badge-check',
          displayValue: this.formatNumber(metrics.liveTrades?.value || 0),
          period: this.getPeriodText()
        },
        {
          key: 'avgVolume',
          label: this.$t('broker.dashboard.metrics.avgTradeVolume') || 'Ave. Trade Volume',
          icon: 'fa-regular fa-chart-line',
          displayValue: metrics.avgTradeVolume ? metrics.avgTradeVolume.value : '0',
          displayUnit: this.$t('broker.dashboard.metrics.units.lots') || 'Lots',
          period: this.getPeriodText()
        },
        {
          key: 'activeTraders',
          label: this.$t('broker.dashboard.metrics.activeTraders') || 'Active Traders',
          icon: 'fa-regular fa-running',
          displayValue: this.formatNumber(metrics.activeTraders?.value || 0),
          period: this.getPeriodText()
        }
      ]
    },
    isDateRangeValid() {
      return this.customDateRange.start && this.customDateRange.end &&
             new Date(this.customDateRange.start) <= new Date(this.customDateRange.end)
    },
    userProfilePicture() {
      const user = this.$store.getters['broker/currentUser']
      return user?.imageUrl || null
    }
  },
  watch: {
    // Only update stableProfilePicture when we have a valid new value
    // This prevents flash to default during route transitions
    userProfilePicture: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.stableProfilePicture = newVal
        }
      }
    }
  },
  methods: {
    forceBlur() {
      // Force blur when the select loses focus
      if (this.$refs.filterSelect && this.$refs.filterSelect.$el) {
        const input = this.$refs.filterSelect.$el.querySelector('input')
        if (input) {
          input.blur()
        }
      }
    },
    async handleFilterChange(value) {
      // Force blur to remove focused state after selection
      // Use setTimeout to ensure it happens after Vuetify's internal state updates
      setTimeout(() => {
        if (this.$refs.filterSelect && this.$refs.filterSelect.$el) {
          const input = this.$refs.filterSelect.$el.querySelector('input')
          if (input) {
            input.blur()
          }
          // Also try to blur the component itself
          if (this.$refs.filterSelect.blur) {
            this.$refs.filterSelect.blur()
          }
        }
      }, 100)

      if (value === 'custom') {
        this.customDateDialog = true
        // Reset to previous value until custom date is applied
        this.$nextTick(() => {
          this.selectedFilter = this.lastNonCustomFilter || 'thisMonth'
        })
      } else {
        this.lastNonCustomFilter = value
        const filter = this.timeFilters.find(f => f.value === value)
        this.selectedFilterLabel = filter ? filter.label : 'This Month'
        await this.loadDashboardData()
      }
    },
    async selectTimeFilter(filter) {
      // Legacy method - keeping for compatibility
      if (filter.value === 'custom') {
        this.customDateDialog = true
      } else {
        this.selectedFilter = filter.value
        this.selectedFilterLabel = filter.label
        await this.loadDashboardData()
      }
    },
    async loadDashboardData() {
      this.isLoading = true
      this.loadingMessage = 'Loading dashboard data...'
      try {
        console.log('Loading dashboard data for filter:', this.selectedFilter)
        const data = await dashboardService.getDashboardData(this.selectedFilter)
        console.log('Dashboard data received:', data)
        this.dashboardData = data

        // Update trader stats from real data
        if (data.stats) {
          this.traderStats = {
            totalTraders: data.stats.totalTraders,
            liveAccountsPercentage: data.stats.liveAccountsPercent,
            demoAccountsPercentage: data.stats.demoAccountsPercent,
            newTradersThisMonth: 0, // Not available in current API
            activeTradersToday: data.stats.activeTraders
          }
        }

        // Update geographic data for map with all required fields
        if (data.mapData && data.rawData) {
          this.geographicData = []

          // Create map from raw country analytics indexed by normalized country code
          const countryDataMap = {}
          if (data.rawData.countryAnalytics) {
            data.rawData.countryAnalytics.forEach(country => {
              // Normalize the country name to get its ISO code
              const normalizedCode = this.normalizeCountryCode(country.country)

              countryDataMap[normalizedCode] = {
                fullName: country.country,
                traders: country.countryTotalTrades,
                activeTrades: country.countryActiveTrades,
                profit: country.countryNetProfit,
                avgLotsPerTrade: country.countryAvgLotsPerTrade
              }
            })
          }

          // Build geographic data array with all fields
          data.mapData.forEach((value, countryCode) => {
            const additionalData = countryDataMap[countryCode] || {}

            // Get the full country name from the raw data
            let fullCountryName = countryCode.toUpperCase()
            if (additionalData.fullName) {
              fullCountryName = this.formatCountryName(additionalData.fullName)
            }

            this.geographicData.push({
              country: fullCountryName, // Full country name for display
              countryCode: countryCode, // lowercase ISO code for SVG matching
              traders: additionalData.traders || 0,
              volume: value.volume,
              percentage: value.percentage,
              profit: additionalData.profit || 0,
              avgLotsPerTrade: additionalData.avgLotsPerTrade || 0
            })
          })

          console.log('Geographic data formatted for map:', this.geographicData)
        }

        // Update performance metrics
        if (data.stats) {
          this.performanceMetrics = {
            volumeOperated: {
              value: data.stats.totalVolume,
              change: 0,
              trend: 'up'
            },
            generatedLots: {
              value: data.stats.totalVolume,
              change: 0,
              trend: 'up'
            },
            liveTrades: {
              value: data.rawData?.activeTrades || 0,
              change: 0,
              trend: 'up'
            },
            avgTradeVolume: {
              value: data.rawData?.avgLotsPerTrade?.toFixed(2) || '0',
              change: 0,
              trend: 'up'
            },
            activeTraders: {
              value: data.stats.activeTraders,
              change: 0,
              trend: 'up'
            }
          }
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error)
      } finally {
        this.isLoading = false
      }
    },
    onCountryClick(country) {
      // Handle country click event
      console.log('Country clicked:', country)
      // Could show detailed stats or navigate to country-specific view
    },
    formatNumber(num) {
      return dashboardService.formatNumber(num)
    },
    normalizeCountryCode(countryName) {
      // Normalize country names to ISO codes (same as repository)
      const nameMap = {
        'CANADA': 'ca',
        'Canada': 'ca',
        'Colombia': 'co',
        'COLOMBIA': 'co',
        'ECUADOR': 'ec',
        'Ecuador': 'ec',
        'GERMANY': 'de',
        'Germany': 'de',
        'INDIA': 'in',
        'India': 'in',
        'INDONESIA': 'id',
        'Indonesia': 'id',
        'KENYA': 'ke',
        'Kenya': 'ke',
        'MALAYSIA': 'my',
        'Malaysia': 'my',
        'MEXICO': 'mx',
        'Mexico': 'mx',
        'NIGERIA': 'ng',
        'Nigeria': 'ng',
        'PHILIPPINES': 'ph',
        'Philippines': 'ph',
        'SOUTH AFRICA': 'za',
        'South Africa': 'za',
        'THAILAND': 'th',
        'Thailand': 'th',
        'UNITED ARAB EMIRATES': 'ae',
        'United Arab Emirates': 'ae',
        'United States of America': 'us',
        'USA': 'us',
        'UZBEKISTAN': 'uz',
        'Uzbekistan': 'uz',
        'VIET NAM': 'vn',
        'Vietnam': 'vn',
        'Viet Nam': 'vn',
        'Argentina': 'ar',
        'ARGENTINA': 'ar',
        'BOLIVIA': 'bo',
        'Bolivia': 'bo',
        'COSTA RICA': 'cr',
        'Costa Rica': 'cr',
        'GUATEMALA': 'gt',
        'Guatemala': 'gt',
        'PANAMA': 'pa',
        'Panama': 'pa'
      }

      // Try exact match first
      const code = nameMap[countryName]
      if (code) return code

      // Try case-insensitive match
      const upperCountry = countryName.toUpperCase()
      for (const [key, value] of Object.entries(nameMap)) {
        if (key.toUpperCase() === upperCountry) {
          return value
        }
      }

      // Default to first two letters lowercased
      return countryName.substring(0, 2).toLowerCase()
    },
    formatCountryName(countryName) {
      // Format country names to proper case
      const nameMap = {
        'CANADA': 'Canada',
        'COLOMBIA': 'Colombia',
        'ECUADOR': 'Ecuador',
        'GERMANY': 'Germany',
        'INDIA': 'India',
        'INDONESIA': 'Indonesia',
        'KENYA': 'Kenya',
        'MALAYSIA': 'Malaysia',
        'MEXICO': 'Mexico',
        'NIGERIA': 'Nigeria',
        'PHILIPPINES': 'Philippines',
        'SOUTH AFRICA': 'South Africa',
        'THAILAND': 'Thailand',
        'UNITED ARAB EMIRATES': 'United Arab Emirates',
        'UNITED STATES': 'United States',
        'USA': 'United States',
        'UZBEKISTAN': 'Uzbekistan',
        'VIET NAM': 'Vietnam',
        'VIETNAM': 'Vietnam',
        'ARGENTINA': 'Argentina',
        'Argentina': 'Argentina',
        'BOLIVIA': 'Bolivia',
        'Bolivia': 'Bolivia',
        'COSTA RICA': 'Costa Rica',
        'Costa Rica': 'Costa Rica',
        'GUATEMALA': 'Guatemala',
        'Guatemala': 'Guatemala',
        'PANAMA': 'Panama',
        'Panama': 'Panama'
      }

      // Check if we have a mapping for this country
      const upperName = countryName.toUpperCase()
      if (nameMap[upperName]) {
        return nameMap[upperName]
      }

      // Otherwise, format the name to proper case
      return countryName.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },
    cancelCustomDate() {
      this.customDateDialog = false
      this.customDateRange = { start: null, end: null }
    },
    async applyCustomDate() {
      if (this.isDateRangeValid) {
        this.selectedFilter = 'custom'
        const startDate = new Date(this.customDateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        const endDate = new Date(this.customDateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        this.selectedFilterLabel = `${startDate} - ${endDate}`
        this.customDateDialog = false
        await this.loadDashboardData()
      }
    },
    getPeriodText() {
      // Show period based on selected filter
      const filter = this.timeFilters.find(f => f.value === this.selectedFilter)
      if (filter) {
        return filter.label
      }

      if (this.selectedFilter === 'custom') {
        if (this.customDateRange.start && this.customDateRange.end) {
          const days = Math.ceil((new Date(this.customDateRange.end) - new Date(this.customDateRange.start)) / (1000 * 60 * 60 * 24)) + 1
          return this.$t('broker.dashboard.filters.customDaysSelected', { days }) || `${days} days selected`
        }
        return this.$t('broker.dashboard.filters.customPeriod') || 'Custom period'
      }

      return this.$t('broker.dashboard.filters.thisMonth') || 'This Month'
    },
    openSupport(){
      window.open("https://swiset.atlassian.net/servicedesk/customer/portal/1", "_blank")
    },
    openReleases() {
    window.open("https://sfb-releases.swiset.com/", "_blank")
  }

  },
  async mounted() {
    // Load user info from store
    const user = this.$store.getters['broker/currentUser']
    if (user) {
      // Initialize stable profile picture
      if (user.imageUrl) {
        this.stableProfilePicture = user.imageUrl
      }

      // Use firstName if available, otherwise use username
      if (user.firstName && user.lastName) {
        this.userName = `${user.firstName} ${user.lastName}`
      } else if (user.firstName) {
        this.userName = user.firstName
      } else {
        this.userName = user.username
      }
    }

    // Load dashboard data with initial loading state
    this.isInitialLoading = true
    try {
      await this.loadDashboardData()
    } finally {
      // Add a small delay to make the transition smoother
      setTimeout(() => {
        this.isInitialLoading = false
      }, 500)
    }
  }
}
</script>

<style lang="scss" scoped>
.broker-dashboard {
  padding: 0;

  .avatar-border {
    border: 3px solid #28ea96;
  }

  .time-filter-select {
    max-width: 250px;
    min-width: 200px;
  }

  .metric-card {
    height: 100%;
    border: 1px solid #e0e0e0 !important;
    transition: border-color 0.2s;
    background: white !important;
    box-shadow: none !important;

    &:hover {
      border-color: #d0d0d0 !important;
    }
  }

  .map-card {
    width: 90%;
    background: transparent !important;
    box-shadow: none !important;

    .v-card__title {
      padding-bottom: 8px;
    }
  }

  .account-type {
    .v-progress-linear {
      background-color: #f5f5f5;
    }
  }

  .trader-growth {
    .v-chip {
      font-weight: 500;
    }
  }

  .gap-3 { gap: 12px; }

  .quick-card{
    cursor: pointer;
    // min-width: 130px;
    height: 44px;
    display: flex;
    align-items: center;
    text-decoration: none;
    border-radius: 999px !important;
  }

  .quick-card .v-card__text{
    padding: 0 14px;
    color: #fff;
  }

  .quick-card:hover{
    border-color: #28EA96;
  }

  .quick-card::before {
    opacity: 0 !important;
  }

  .quick-card:focus::before,
  .quick-card:hover::before,
  .quick-card:active::before {
    opacity: 0 !important;
  }
}

</style>